import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

export interface DomainProductLinesDelegateParams {
  rateSegment: RateSegment;
  getCoveredProductLineIds?: () => ProductLineId[];
}

export interface DomainProductLineItem {
  productLineId: ProductLineId;
  rateSegment: RateSegment;
  isAvailableWithCurrentConfiguration: boolean;
  /**
   * True when this line is on a renewal and change-plan is not allowed, so it cannot be quoted again.
   * The chip is shown as unavailable instead of being removed from the list.
   */
  isLocked?: boolean;
}

export class DomainProductLinesDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _rateSegment: RateSegment;
  private readonly _getCoveredProductLineIds?: () => ProductLineId[];
  private _domainProductLineItems: DomainProductLineItem[] = [];
  private _isLoading = false;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this._domainProductLineItems.filter((item) => item.rateSegment === this._rateSegment);
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  /** True when at least one offered line in this rate segment is not locked. */
  public get hasQuotableProductLine(): boolean {
    return this.domainProductLineItems.some((item) => item.isAvailableWithCurrentConfiguration && !item.isLocked);
  }

  /** True when the domain offered at least one line for the selected date/ZIP, on any rate segment. */
  public get hasAvailableProductLineForAnyRateSegment(): boolean {
    return this._domainProductLineItems.some((item) => item.isAvailableWithCurrentConfiguration);
  }

  private _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  public clearProductLines(): void {
    this._domainProductLineItems = [];
  }

  public async getDomainProductLines(
    formData: Record<string, string>,
    includeUnavailable: boolean = true,
  ): Promise<void> {
    this._setIsLoading(true);
    const { effectiveDate, zip, state, county, sic4 } = formData;
    const productLines = await getDomainProductLine({
      effectiveDate,
      zipCode: zip,
      state,
      county,
      agencyId: this._rootStore.userStore.imAgencyId,
      sponsorId: this._rootStore.userStore.sponsorId,
      ...(sic4 ? { sic: sic4 } : {}),
      includeUnavailable,
    });

    runInAction(() => {
      // Initiate quote: lock installed lines unless change-plan is on. Proposal info omits getCoveredProductLineIds, so nothing is locked.
      const lockedProductLineIds = this._rootStore.domainStoreV2.isChangePlanActionAvailable(
        this._rootStore.userStore.userRoles,
      )
        ? []
        : this._getCoveredProductLineIds?.() ?? [];

      this._domainProductLineItems = productLines
        .map((productLine) => ({
          productLineId: productLine.productLineId,
          rateSegment: productLine.rateSegment,
          isAvailableWithCurrentConfiguration: productLine.available,
          isLocked: lockedProductLineIds.includes(productLine.productLineId),
        }))
        .sort((a, b) => sortByProductLineId(a.productLineId, b.productLineId));
      this._setIsLoading(false);
    });
  }

  constructor(params: DomainProductLinesDelegateParams) {
    this._rateSegment = params.rateSegment;
    this._getCoveredProductLineIds = params.getCoveredProductLineIds;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
